/**
 * **Rückgängig** und **Wiederherstellen** im Projekt-Canvas (siehe
 * CONTEXT.md).
 *
 * Die ganze Sache steht auf einer Beobachtung: **Jede Canvas-Mutation lässt
 * sich als „setze diese Felder auf diesen Zeilen" ausdrücken — auch das
 * Anlegen und das Löschen.** `deleteCanvasNote` ist ein Soft-Delete über
 * `deletedAt`, und `createCanvasNote` legt eine Zeile mit `deletedAt: null`
 * an:
 *
 * | Schritt | zurück | vor |
 * |---|---|---|
 * | Anlegen | `deletedAt: <jetzt>` | `deletedAt: null` |
 * | Löschen | `deletedAt: null` | `deletedAt: <jetzt>` |
 * | Ändern | die alten Werte | die neuen Werte |
 *
 * Ein Schritt ist damit einheitlich ein Paar von Feldänderungen je Zeile.
 * Rückgängig wendet `before` an, Wiederherstellen `after` — **ein Weg, zwei
 * Richtungen**.
 *
 * **Eine Ausnahme, und nur eine:** Die **Übernahme** legt Aufgaben in der
 * **Aufgabenliste** an, und die lassen sich nicht als Notizfelder ausdrücken.
 * Ihre Kennungen hängen deshalb als `createdTaskIds` am Schritt (ADR 0011).
 *
 * **Warum ein zurückgenommenes Anlegen die Zeile nicht wirklich löscht:** Der
 * Notiz behielte sonst seine Kennung nicht. Jeder ältere Schritt im Stapel,
 * der auf ihn zeigt, zeigte nach dem Wiederherstellen ins Leere — ebenso die
 * Aufgaben-Verbindung (`taskId`) und die Cloud-Zeile (`cloudId`).
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: Der Stapel soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import type { UpdateCanvasNote } from '@/src/renderer/lib/db/models'

/**
 * Wie viele Schritte der Stapel hält.
 *
 * Jeder ist eine Handvoll Zahlen; die Grenze schützt vor einer Sitzung, die
 * stundenlang läuft, und liegt weit jenseits dessen, was jemand zurückgehen
 * will.
 */
export const HISTORY_LIMIT = 100

/** Eine Zeile eines Schritts: wohin zurück, wohin vor. */
export interface HistoryEntry {
  id: string
  before: UpdateCanvasNote
  after: UpdateCanvasNote
}

/**
 * Ein Schritt — das, was **ein** ⌘Z zurücknimmt.
 *
 * Mehrere Einträge, weil ein Griff mehrere Notizen bewegt: Fünf ausgewählte
 * zu verschieben sind fünf Schreibvorgänge und trotzdem ein Schritt.
 */
export interface HistoryStep {
  entries: HistoryEntry[]
  /**
   * Die Aufgaben, die dieser Schritt in der **Aufgabenliste** angelegt hat —
   * gesetzt allein von der **Übernahme** (ADR 0011).
   *
   * Hier bricht die Geschlossenheit des Stapels auf, und zwar mit Ansage:
   * Alles andere hier ist „setze diese Felder auf diesen Zeilen", das hier ist
   * es nicht. Die Begründung steht in CONTEXT.md unter **Rückgängig** — *die
   * Grenze ist die Herkunft, nicht die Tabelle*. Was der Canvas selbst
   * angelegt hat, nimmt er auch zurück; ein ⌘Z, das die Ecke aufklappt und die
   * Aufgabe stehen ließe, legte beim nächsten Übernehmen dieselbe Aufgabe ein
   * zweites Mal an.
   *
   * Wer eine weitere canvasfremde Wirkung hinzufügt, geht denselben Weg,
   * statt eine dritte Art zu erfinden.
   */
  createdTaskIds?: string[]
  /**
   * Worauf die Auswahl nach Rückgängig/Wiederherstellen zeigt — wenn nicht
   * auf alle Betroffenen. Beim **Platz tauschen** und **Dazwischen schieben**
   * haben die übrigen nur Platz gemacht: Ausgewählt wird, was man angefasst
   * hatte (CONTEXT.md, Rückgängig). Ohne Angabe: alle Einträge des Schritts.
   */
  select?: string[]
}

export interface CanvasHistoryState {
  /** Die zurücknehmbaren Schritte, ältester zuerst. */
  undo: HistoryStep[]
  /** Die wiederherstellbaren, ältester zuerst. */
  redo: HistoryStep[]
}

export const EMPTY_HISTORY: CanvasHistoryState = { undo: [], redo: [] }

/**
 * Einen Schritt oben auflegen.
 *
 * **Der Wiederherstellen-Stapel wird geleert.** Die übliche Regel: Wer nach
 * einem Rückgängig etwas Neues tut, verzweigt die Geschichte — und die
 * Verzweigung, die niemand mehr sieht, wird verworfen.
 *
 * Ein Schritt ohne Einträge ändert nichts: Er entstünde etwa, wenn eine Geste
 * abgebrochen wurde, und ein leeres ⌘Z, das scheinbar nichts tut, wäre der
 * verwirrendste Zustand von allen.
 */
export function pushStep(state: CanvasHistoryState, step: HistoryStep): CanvasHistoryState {
  // Aufgaben zählen mit: Ein Schritt kann Wirkung haben, ohne ein Notizfeld
  // anzufassen — theoretisch jedenfalls. Ihn hier zu verschlucken wäre genau
  // das leere ⌘Z, das dieser Wächter verhindern soll.
  if (step.entries.length === 0 && (step.createdTaskIds?.length ?? 0) === 0) return state
  return {
    undo: [...state.undo, step].slice(-HISTORY_LIMIT),
    redo: [],
  }
}

/**
 * Ein Element aus dem Stapel tilgen, als hätte es es nie gegeben — alle
 * Einträge zu dieser Kennung, in beiden Richtungen. Ein Schritt, der danach
 * leer ist, fällt mit weg (siehe `pushStep`: ein leeres ⌘Z wäre das
 * Verwirrendste).
 *
 * Für den **Text**, der frisch abgesetzt und leer wieder verlassen wird: Er
 * geht spurlos (CONTEXT.md, Text). Blieb sein Anlegen im Stapel, holte das
 * nächste ⌘Z einen unsichtbaren, leeren Text zurück.
 */
export function forgetNote(state: CanvasHistoryState, id: string): CanvasHistoryState {
  const strip = (steps: HistoryStep[]) => steps
    .map(step => (step.entries.some(entry => entry.id === id)
      ? { ...step, entries: step.entries.filter(entry => entry.id !== id) }
      : step))
    .filter(step => step.entries.length > 0 || (step.createdTaskIds?.length ?? 0) > 0)
  return { undo: strip(state.undo), redo: strip(state.redo) }
}

/** Was ein Rückgängig oder Wiederherstellen ergibt. */
export interface HistoryMove {
  /** Der neue Stapelzustand. */
  state: CanvasHistoryState
  /**
   * Die anzuwendenden Änderungen, in der Reihenfolge des Schritts. Leer, wenn
   * es nichts zurückzunehmen gab — der Aufrufer schreibt dann nicht.
   */
  changes: { id: string; updates: UpdateCanvasNote }[]
  /**
   * Die Aufgaben des Schritts und was mit ihnen geschehen soll: Beim
   * Rückgängig `'delete'`, beim Wiederherstellen `'restore'`. Leer, wenn der
   * Schritt keine angelegt hat — der Normalfall.
   */
  tasks: { ids: string[]; action: 'delete' | 'restore' }
  /** Worauf die Auswahl danach zeigt — `null` heißt: auf alle Betroffenen. */
  select: string[] | null
}

const NO_TASKS = { ids: [] as string[], action: 'delete' as const }

const NOTHING = (state: CanvasHistoryState): HistoryMove => ({ state, changes: [], tasks: NO_TASKS, select: null })

/**
 * Den obersten Schritt zurücknehmen: seine `before`-Werte anwenden.
 *
 * Die Reihenfolge der Einträge bleibt, wie sie aufgezeichnet wurde. Beim
 * Canvas spielt sie keine Rolle — die Einträge betreffen verschiedene Zeilen —,
 * aber sie umzudrehen wäre eine Behauptung über Abhängigkeiten, die es nicht
 * gibt.
 */
export function undo(state: CanvasHistoryState): HistoryMove {
  const step = state.undo.at(-1)
  if (!step) return NOTHING(state)

  return {
    state: { undo: state.undo.slice(0, -1), redo: [...state.redo, step] },
    changes: step.entries.map(entry => ({ id: entry.id, updates: entry.before })),
    // Zurück heißt hier: die Aufgaben wieder weg. Weich gelöscht wie überall —
    // ein Wiederherstellen muss sie zurückholen können.
    tasks: { ids: step.createdTaskIds ?? [], action: 'delete' },
    select: step.select ?? null,
  }
}

/** Den zuletzt zurückgenommenen Schritt wieder anwenden: seine `after`-Werte. */
export function redo(state: CanvasHistoryState): HistoryMove {
  const step = state.redo.at(-1)
  if (!step) return NOTHING(state)

  return {
    state: { undo: [...state.undo, step], redo: state.redo.slice(0, -1) },
    changes: step.entries.map(entry => ({ id: entry.id, updates: entry.after })),
    tasks: { ids: step.createdTaskIds ?? [], action: 'restore' },
    select: step.select ?? null,
  }
}

/**
 * Sammelt die Einträge EINES Schritts.
 *
 * Ausdrücklich gebündelt und nicht geraten: Schreibvorgänge innerhalb eines
 * Frames automatisch zusammenzufassen sähe einfacher aus, risse aber einen
 * unbeteiligten Schreibvorgang im selben Tick mit hinein (etwa das Umhängen
 * der Aufgaben-Verbindung beim Alt-Ziehen) — und man merkte es erst, wenn ⌘Z
 * zu viel zurücknimmt.
 */
export class StepRecorder {
  private readonly entries: HistoryEntry[] = []
  private readonly taskIds: string[] = []
  private selection: string[] | null = null

  /**
   * Eine Änderung an einer bestehenden Zeile.
   *
   * @param before die alten Werte GENAU der Felder, die `after` setzt — der
   *               Aufrufer liest sie vor dem Schreiben aus der Datenbank
   */
  update(id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) {
    this.entries.push({ id, before, after })
  }

  /**
   * Eine neu angelegte Zeile. Zurück heißt: wieder verbergen.
   *
   * Der Zeitstempel kommt vom Aufrufer und nicht aus `new Date()` hier:
   * Sämtliche Einträge eines Schritts sollen denselben tragen, sonst
   * unterschieden sich die Zeilen einer gemeinsamen Geste um Millisekunden.
   */
  created(id: string, at: Date) {
    this.entries.push({ id, before: { deletedAt: at }, after: { deletedAt: null } })
  }

  /** Eine gelöschte Zeile. Zurück heißt: wieder hervorholen. */
  deleted(id: string, at: Date) {
    this.entries.push({ id, before: { deletedAt: null }, after: { deletedAt: at } })
  }

  /**
   * Die Aufgaben, die diese Geste in der Aufgabenliste angelegt hat — nur die
   * **Übernahme** benutzt das (ADR 0011).
   */
  createdTasks(ids: string[]) {
    this.taskIds.push(...ids)
  }

  /**
   * Worauf die Auswahl nach Rückgängig/Wiederherstellen zeigt — statt auf
   * alle Betroffenen (siehe `HistoryStep.select`).
   */
  select(ids: readonly string[]) {
    this.selection = [...ids]
  }

  /** Der fertige Schritt. */
  finish(): HistoryStep {
    return {
      entries: this.entries,
      ...(this.taskIds.length > 0 ? { createdTaskIds: this.taskIds } : {}),
      ...(this.selection ? { select: this.selection } : {}),
    }
  }
}
